<template>
  <section
    v-show="
      !alternativeStays.loading.value && alternativeStays.data.value.length
    "
    :class="{ desktop: $isDesktop }"
  >
    <h4 class="alternative-stays">
      <img
        class="icon"
        src="@/assets/images/calendar-star-blue.svg"
        width="30"
        height="30"
      />
      <translate>Alternative dates</translate>
    </h4>
    <ul>
      <li v-for="stay in alternativeStays.data.value" :key="stay.fulldate">
        <NuxtLink :to="generateStayLink(stay)">
          <div class="col">
            <div class="dates">
              <pu-date :date="stay.arrive" options="noYear" />
              -
              <pu-date :date="stay.depart" options="noYear" />
              <pu-date :date="stay.depart" options="year" class="year" />
            </div>
            <span class="nights">
              <translate :tt="{ nights: calculateNights(stay) }">
                {nights, plural, one{# night} other{# nights}}
              </translate>
            </span>
            <pu-price :price="stay.price" :display-original-price="true" />
          </div>
          <button class="button sml">
            <translate>Choose</translate>
          </button>
        </NuxtLink>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { ComputedRef } from 'vue'
import { CalendarDay } from '../../../../alternativeStaysFetch'
import { daysBetween } from '../../../../../../utility/date/relative'

const route = useRoute()

const alternativeStays = inject('alternativeStays') as {
  data: ComputedRef<CalendarDay[]>
  loading: ComputedRef<boolean>
  error: ComputedRef<Error | null>
}

const generateStayLink = (stay: CalendarDay) => {
  const currentQuery = { ...route.query }
  return {
    query: {
      ...currentQuery,
      arrive: stay.arrive,
      depart: stay.depart,
    },
  }
}

const calculateNights = (stay: CalendarDay) => {
  return daysBetween(stay.depart, stay.arrive)
}
</script>

<style scoped>
h4.alternative-stays {
  display: flex;
  align-items: center;
  color: var(--colour-price);
}

h4 .icon {
  margin-right: 0.4em;
  align-self: flex-start;
}

.dates {
  font-weight: bold;
  width: 100%;
  color: #333;
  display: inline;
}

.desktop .dates {
  display: block;
}

.dates .year {
  font-weight: normal;
}

ul {
  margin-top: 0.5em;
  border-bottom: 1px solid var(--colour-page-background);
  container: alternative-dates / inline-size;
}

li {
  border-top: 1px solid var(--colour-page-background);
}

li a {
  text-decoration: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  padding: 0.3em 0;
}

li a:deep(.display-price) {
  font-size: inherit;
  color: #333 !important;
}

button {
  margin: auto 0 auto 0.4em !important;
  border-radius: 2em;
  box-shadow: none;
  font-weight: normal;
  border-color: var(--colour-price);
  padding: 0.3em 0.5em;
}

li a:active,
li a:focus-visible {
  background: #eee;
  box-shadow:
    -0.3em 0 0 0 #eee,
    0.3em 0 0 0em #eee;
  border-radius: 0.25em;
}

li a:active button {
  background: var(--colour-light-green);
  border-color: var(--colour-light-green);
  color: white;
  transform: none;
  box-shadow: none;
}

@media (hover: hover) {
  li a:hover {
    background: #eee;
    box-shadow:
      -0.3em 0 0 0 #eee,
      0.3em 0 0 0em #eee;
    border-radius: 0.25em;
  }

  li a:hover button,
  button:hover {
    background: var(--colour-light-green);
    border-color: var(--colour-light-green);
    color: white;
    transform: none;
    box-shadow: none;
  }

  li:has(a:hover) + li,
  li:has(a:hover) {
    border-color: transparent;
  }
}

@container alternative-dates (width > 44ch) {
  .desktop .dates {
    display: inline;
  }
}
</style>
